/**

 * MemorialTab — private Memorial Mode workspace on person pages.

 */

import React, { useCallback, useEffect, useState } from 'react';

import axios from 'axios';

import {

  BookOpen,

  Mic,

  Image,

  FileText,

  QrCode,

  Share2,

  Eye,

  Flame,

  Lock,

  Loader2,

  AlertCircle,

  Users,

  Link2,

  Globe,

  Copy,

  Download,

} from 'lucide-react';

import { Button } from '../ui/button';

import {

  Dialog,

  DialogContent,

  DialogDescription,

  DialogHeader,

  DialogTitle,

} from '../ui/dialog';

import { toast } from 'sonner';

import { cn } from '@/lib/utils';

import { getApiBase, getBackendBaseUrl } from '../../utils/apiBase';

import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

import {

  getMemorialPublicUrl,

  getMemorialStatusLabel,

  getMemorialVisibilityLabel,

  isMemorialPublished,

  MEMORIAL_COPY,

} from '../../utils/memorial';

import {
  MEMORIAL_BRIDGE_PILLAR_LABEL,
  MEMORIAL_BRIDGE_WORKSPACE_TAGLINE,
} from '../../content/memorialBridgeContent';

import { ManageMemorialTributesDialog } from './ManageMemorialTributesDialog';

import { MemorialWorkspaceControls } from './MemorialWorkspaceControls';

import { MemorialVisibilityDialog } from './MemorialSettingsPanels';

import { SoftUpgradePrompt } from '../billing/SoftUpgradePrompt';

import { useUsageLimits } from '../../hooks/useUsageLimits';

import { shouldShowMemorialLifetimePrompt } from '../../utils/usageLimits';



const API = getApiBase();

const BACKEND = getBackendBaseUrl();



const CONTENT_SECTIONS = [

  { icon: BookOpen, title: 'Life story', key: 'life_story' },

  { icon: Image, title: 'Photo memories', key: 'photos' },

  { icon: Mic, title: 'Voice memories', key: 'voice' },

  { icon: FileText, title: 'Documents and keepsakes', key: 'documents' },

];



function StatusBadge({ children, tone = 'muted' }) {

  const tones = {

    muted: 'bg-muted/40 text-muted-foreground border-border/50',

    draft: 'bg-amber-50 text-amber-900 border-amber-200/80',

    published: 'bg-emerald-50 text-emerald-900 border-emerald-200/80',

    private: 'bg-slate-50 text-slate-700 border-slate-200/80',

  };

  return (

    <span

      className={cn(

        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium',

        tones[tone] || tones.muted

      )}

    >

      {children}

    </span>

  );

}



function WorkspaceCard({ title, description, children, className, testId }) {

  return (

    <div

      data-testid={testId}

      className={cn(

        'flex flex-col rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5 shadow-[inset_0_1px_0_0_hsla(0,0%,100%,0.5)]',

        className

      )}

    >

      <h3 className="font-serif text-base font-light text-[#1c1917]">{title}</h3>

      {description ? (

        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground/90">{description}</p>

      ) : null}

      <div className="mt-4 flex flex-1 flex-col gap-2">{children}</div>

    </div>

  );

}



export function MemorialTab({

  person,

  canEdit = false,

  storyCount = 0,

  documentCount = 0,

  onMemorialUpdated,

  onRefreshPerson,

}) {

  const [workspace, setWorkspace] = useState(null);

  const [loading, setLoading] = useState(true);

  const [enabling, setEnabling] = useState(false);

  const [publishing, setPublishing] = useState(false);

  const [tributesOpen, setTributesOpen] = useState(false);

  const [visibilityOpen, setVisibilityOpen] = useState(false);

  const [qrOpen, setQrOpen] = useState(false);

  const [qrBlobUrl, setQrBlobUrl] = useState(null);



  const wsPerson = workspace?.person || person;

  const memorialTreeId = wsPerson?.tree_id || person?.tree_id;

  const { snapshot: usageSnapshot } = useUsageLimits({
    treeId: memorialTreeId,
    enabled: Boolean(memorialTreeId),
  });

  const memorial = workspace?.memorial;

  const name = [wsPerson?.given_names, wsPerson?.surname].filter(Boolean).join(' ') || 'This person';

  const published = isMemorialPublished(memorial);

  const publicUrl = getMemorialPublicUrl(workspace?.public_url, memorial?.slug);

  const photo = wsPerson?.profile_image_url

    ? resolveAssetUrl(BACKEND, wsPerson.profile_image_url)

    : null;



  const loadWorkspace = useCallback(async () => {

    if (!person?.id) return;

    setLoading(true);

    try {

      const { data } = await axios.get(`${API}/memorials-api/by-person/${person.id}`);

      setWorkspace(data);

    } catch {

      toast.error('Could not load Memorial Mode workspace.');

    } finally {

      setLoading(false);

    }

  }, [person?.id]);



  useEffect(() => {

    loadWorkspace();

  }, [loadWorkspace]);



  useEffect(() => {

    if (!qrOpen || !published || !memorial?.id) {

      setQrBlobUrl(null);

      return undefined;

    }

    let cancelled = false;

    (async () => {

      try {

        const res = await axios.get(`${API}/memorials-api/${memorial.id}/qr.png`, { responseType: 'blob' });

        if (!cancelled) setQrBlobUrl(URL.createObjectURL(res.data));

      } catch {

        if (!cancelled) toast.error('Could not load QR code.');

      }

    })();

    return () => {

      cancelled = true;

      setQrBlobUrl((prev) => {

        if (prev) URL.revokeObjectURL(prev);

        return null;

      });

    };

  }, [qrOpen, published, memorial?.id]);



  const handleEnableMemorial = async () => {

    if (!canEdit) return;

    setEnabling(true);

    try {

      const resp = await axios.patch(`${API}/individuals/${person.id}/memorial`, {

        life_state: 'remembered',

        memorial_mode_enabled: true,

      });

      onMemorialUpdated?.(resp.data);

      toast.success('Memorial Mode enabled.', {

        description: `${name}'s private memorial workspace is ready.`,

      });

      await loadWorkspace();

      onRefreshPerson?.();

    } catch (err) {

      toast.error(err?.response?.data?.detail || 'Could not enable Memorial Mode.');

    } finally {

      setEnabling(false);

    }

  };



  const handlePublish = async () => {

    if (!memorial?.id) return;

    setPublishing(true);

    try {

      await axios.post(`${API}/memorials-api/${memorial.id}/publish`, {});

      toast.success('Memorial page published.', {

        description: 'Your memorial link is now available. Content still respects your visibility settings.',

      });

      await loadWorkspace();

      onRefreshPerson?.();

    } catch (err) {

      toast.error(err?.response?.data?.detail || 'Could not publish memorial.');

    } finally {

      setPublishing(false);

    }

  };



  const handleUnpublish = async () => {

    if (!memorial?.id) return;

    try {

      await axios.post(`${API}/memorials-api/${memorial.id}/unpublish`);

      toast.message('Memorial unpublished.', { description: 'The public link is no longer available.' });

      await loadWorkspace();

      onRefreshPerson?.();

    } catch {

      toast.error('Could not unpublish memorial.');

    }

  };



  const copyLink = async () => {

    if (!publicUrl) return;

    try {

      await navigator.clipboard.writeText(publicUrl);

      toast.success('Memorial link copied.');

    } catch {

      toast.error('Could not copy link.');

    }

  };



  const downloadQr = async () => {

    if (!memorial?.id) return;

    try {

      const res = await axios.get(`${API}/memorials-api/${memorial.id}/qr.png`, { responseType: 'blob' });

      const url = URL.createObjectURL(res.data);

      const a = document.createElement('a');

      a.href = url;

      a.download = `memorial-${memorial.slug || 'qr'}.png`;

      a.click();

      URL.revokeObjectURL(url);

    } catch {

      toast.error('Could not download QR code.');

    }

  };



  if (loading && !workspace) {

    return (

      <div className="flex justify-center py-16" data-testid="memorial-tab">

        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-label="Loading memorial workspace" />

      </div>

    );

  }



  const statusLabel = getMemorialStatusLabel(memorial);

  const visibilityLabel = getMemorialVisibilityLabel(memorial, published);

  const pendingTributes = workspace?.tributes_pending_count || 0;

  const statusTone =

    memorial?.status === 'published' ? 'published' : memorial?.status === 'draft' ? 'draft' : 'muted';

  const visibilityTone = published ? 'published' : 'private';



  return (

    <div className="space-y-8" data-testid="memorial-tab">

      {/* Header */}

      <div className="rounded-2xl border border-[hsl(38_16%_82%)]/80 bg-gradient-to-br from-[hsl(42_30%_100%)] to-[hsl(40_22%_97%)] px-6 py-6 sm:px-8 sm:py-7">

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

          <div className="flex min-w-0 items-start gap-3">

            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary)/0.08)] text-primary">

              <Flame className="h-5 w-5" aria-hidden />

            </span>

            <div className="min-w-0">

              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">

                {MEMORIAL_BRIDGE_PILLAR_LABEL}

              </p>

              <h2 className="mt-1 font-serif text-xl font-light text-[#1c1917] sm:text-2xl">Memorial Bridge</h2>

              <p className="mt-1 text-sm text-[#5c534a]">{name}</p>

              <p className="mt-2 max-w-2xl text-[0.9rem] leading-relaxed text-[#6b5f52]">

                {MEMORIAL_BRIDGE_WORKSPACE_TAGLINE}

              </p>

              <div className="mt-3 flex flex-wrap gap-2">

                <StatusBadge tone={statusTone}>{statusLabel}</StatusBadge>

                <StatusBadge tone={visibilityTone}>{visibilityLabel}</StatusBadge>

              </div>

            </div>

          </div>

          {photo ? (

            <img

              src={photo}

              alt=""

              className="h-16 w-16 shrink-0 rounded-full border border-[hsl(38_14%_86%)] object-cover sm:h-20 sm:w-20"

            />

          ) : null}

        </div>

      </div>



      <div className="flex items-start gap-2.5 rounded-xl border border-[hsl(38_14%_86%)]/50 bg-[hsl(42_28%_99%)] px-4 py-3.5">

        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" aria-hidden />

        <div className="space-y-1 text-xs leading-relaxed text-muted-foreground/90">

          <p>

            <span className="font-medium text-foreground/70">Memorial Mode is optional.</span>{' '}

            Marking someone as deceased does not automatically create a public memorial page. You choose what to share,

            and when.

          </p>

          <p>

            Publish this memorial to create a private or public memorial link that can be shared with family. QR codes

            point only to <span className="font-mono text-[10px]">/memorial/:slug</span> — never to private profile

            pages.

          </p>

        </div>

      </div>



      {/* Workspace cards */}

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">

        <WorkspaceCard

          title="Memorial status"

          description="Enable a private workspace before publishing anything."

          testId="memorial-card-status"

        >

          <p className="text-sm font-medium text-foreground/85">{statusLabel}</p>

          {canEdit && (!memorial || memorial.status === 'disabled') ? (

            <Button

              type="button"

              className="mt-auto rounded-full"

              onClick={handleEnableMemorial}

              disabled={enabling}

              data-testid="memorial-enable-btn"

            >

              {enabling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Flame className="mr-2 h-4 w-4" />}

              Enable Memorial Mode

            </Button>

          ) : (

            <p className="text-xs text-muted-foreground">Private workspace active — configure sections below.</p>

          )}

        </WorkspaceCard>



        <WorkspaceCard

          title="Memorial page"

          description="Preview and publish a dedicated memorial URL when you are ready."

          testId="memorial-card-page"

        >

          {usageSnapshot && shouldShowMemorialLifetimePrompt(usageSnapshot) ? (
            <SoftUpgradePrompt
              promptId="memorial-lifetime"
              treeId={memorialTreeId}
              visible
              snapshot={usageSnapshot}
              message="Memorial pages will be offered as a lifetime add-on, not a monthly subscription."
              ctaLabel="Request memorial page"
              ctaTo="/pricing#memorial-page"
              featureName="memorial_page"
              className="mb-3"
              data-testid="soft-upgrade-prompt-memorial"
            />
          ) : null}

          <p className="truncate text-xs text-muted-foreground">

            {published && publicUrl ? publicUrl : 'Link available after publishing'}

          </p>

          <div className="mt-auto flex flex-wrap gap-2">

            <Button

              type="button"

              variant="outline"

              size="sm"

              className="rounded-full"

              disabled={!memorial || memorial.status === 'disabled'}

              onClick={() => {

                if (published && publicUrl) window.open(publicUrl, '_blank', 'noopener,noreferrer');

                else toast.message('Preview available after publishing', {

                  description: 'Publish the memorial page when you are ready to share it.',

                });

              }}

              data-testid="memorial-preview-btn"

            >

              <Eye className="mr-1.5 h-3.5 w-3.5" />

              Preview

            </Button>

            {canEdit && memorial && memorial.status !== 'disabled' ? (

              !published ? (

                <Button

                  type="button"

                  size="sm"

                  className="rounded-full"

                  onClick={handlePublish}

                  disabled={publishing}

                >

                  {publishing ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Share2 className="mr-2 h-3.5 w-3.5" />}

                  Publish

                </Button>

              ) : (

                <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={handleUnpublish}>

                  <Lock className="mr-1.5 h-3.5 w-3.5" />

                  Unpublish

                </Button>

              )

            ) : null}

            <Button

              type="button"

              variant="outline"

              size="sm"

              className="rounded-full"

              disabled={!published}

              onClick={copyLink}

              data-testid="memorial-copy-link-btn"

            >

              <Copy className="mr-1.5 h-3.5 w-3.5" />

              Copy link

            </Button>

          </div>

        </WorkspaceCard>



        <WorkspaceCard

          title="Tributes / guestbook"

          description="Moderation is on by default — you approve messages before they appear."

          testId="memorial-card-tributes"

        >

          <p className="text-sm text-muted-foreground">

            {memorial && memorial.status !== 'disabled'

              ? pendingTributes > 0

                ? `${pendingTributes} pending for review`

                : 'No pending tributes'

              : 'Available after Memorial Mode is enabled'}

          </p>

          <Button

            type="button"

            variant="outline"

            size="sm"

            className="mt-auto w-fit rounded-full"

            disabled={!memorial || memorial.status === 'disabled'}

            onClick={() => setTributesOpen(true)}

            data-testid="memorial-tributes-btn"

          >

            <Users className="mr-1.5 h-3.5 w-3.5" />

            Manage tributes

          </Button>

        </WorkspaceCard>



        <WorkspaceCard

          title="QR code"

          description="For published memorials only — links to /memorial/:slug."

          testId="memorial-card-qr"

        >

          <p className="text-sm text-muted-foreground">

            {published ? 'Ready to share at services or on printed keepsakes.' : 'Available after publishing'}

          </p>

          <div className="mt-auto flex flex-wrap gap-2">

            <Button

              type="button"

              variant="outline"

              size="sm"

              className="rounded-full"

              disabled={!published}

              onClick={() => setQrOpen(true)}

              data-testid="memorial-qr-btn"

            >

              <QrCode className="mr-1.5 h-3.5 w-3.5" />

              View QR

            </Button>

            <Button

              type="button"

              variant="outline"

              size="sm"

              className="rounded-full"

              disabled={!published}

              onClick={downloadQr}

            >

              <Download className="mr-1.5 h-3.5 w-3.5" />

              Download

            </Button>

          </div>

        </WorkspaceCard>



        <WorkspaceCard

          title="Memorial content"

          description="Choose what appears on the memorial page via Configure visibility."

          className="lg:col-span-2 xl:col-span-2"

          testId="memorial-card-content"

        >

          <div className="grid gap-3 sm:grid-cols-2">

            {CONTENT_SECTIONS.map(({ icon: Icon, title }) => (

              <div

                key={title}

                className="flex items-start gap-2.5 rounded-lg border border-[hsl(38_14%_86%)]/45 bg-white/50 px-3 py-2.5"

              >

                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" aria-hidden />

                <div>

                  <p className="text-sm font-medium text-foreground/80">{title}</p>

                  <p className="text-[11px] text-muted-foreground">

                    {title === 'Life story' &&

                      `${storyCount} ${storyCount === 1 ? 'story' : 'stories'} in archive — visibility in settings`}

                    {title === 'Documents and keepsakes' &&

                      `${documentCount} ${documentCount === 1 ? 'document' : 'documents'} in archive`}

                    {(title === 'Photo memories' || title === 'Voice memories') &&

                      'Included only when enabled in visibility settings'}

                  </p>

                </div>

              </div>

            ))}

          </div>

          {canEdit && memorial && memorial.status !== 'disabled' ? (

            <Button

              type="button"

              variant="outline"

              size="sm"

              className="mt-2 w-fit rounded-full"

              onClick={() => setVisibilityOpen(true)}

            >

              <Globe className="mr-1.5 h-3.5 w-3.5" />

              Configure visibility

            </Button>

          ) : null}

        </WorkspaceCard>

      </div>



      {canEdit && memorial && memorial.status !== 'disabled' ? (
        <MemorialWorkspaceControls
          person={person}
          memorial={memorial}
          canEdit={canEdit}
          publicUrl={publicUrl}
          onUpdated={loadWorkspace}
          onOpenVisibility={() => setVisibilityOpen(true)}
          onPublishMemorial={handlePublish}
          publishing={publishing}
        />
      ) : null}



      <ManageMemorialTributesDialog

        open={tributesOpen}

        onOpenChange={setTributesOpen}

        memorialId={memorial?.id}

        onUpdated={loadWorkspace}

      />



      {person ? (

        <MemorialVisibilityDialog

          open={visibilityOpen}

          onOpenChange={setVisibilityOpen}

          personId={person.id}

          person={person}

          onSaved={() => {

            loadWorkspace();

            onRefreshPerson?.();

          }}

        />

      ) : null}



      <Dialog open={qrOpen} onOpenChange={setQrOpen}>

        <DialogContent className="max-w-sm" data-testid="memorial-qr-dialog">

          <DialogHeader>

            <DialogTitle className="font-serif font-light">Memorial QR code</DialogTitle>

            <DialogDescription>Scan to open the published memorial page at /memorial/:slug.</DialogDescription>

          </DialogHeader>

          {published && memorial?.id ? (

            <div className="flex flex-col items-center gap-4 py-2">

              {qrBlobUrl ? (

                <img

                  src={qrBlobUrl}

                  alt="Memorial QR code"

                  className="h-48 w-48 rounded-lg border border-border/50"

                />

              ) : (

                <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />

              )}

              <p className="break-all text-center text-xs text-muted-foreground">{publicUrl}</p>

              <div className="flex gap-2">

                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={copyLink}>

                  <Link2 className="mr-1 h-3 w-3" />

                  Copy link

                </Button>

                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={downloadQr}>

                  <Download className="mr-1 h-3 w-3" />

                  Download

                </Button>

              </div>

            </div>

          ) : null}

        </DialogContent>

      </Dialog>

    </div>

  );

}



export default MemorialTab;

